import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StayOrLeave, WaitThen } from '@app/service/state/state.service';
import { SERVER_BASE_URL } from '../../../environments/environment';
import { Observable, Subscriber } from 'rxjs';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { IframeService } from '../iframe/iframe.service';

/**
 * Redirect service for checkout.
 * Redirects to merchant success URL after the backend has called the merchant callback endpoint.
 */
@Injectable({
  providedIn: 'root',
})
export class RedirectService {
  private readonly endpoints: Map<CheckoutType, string> = new Map([
    [
      CheckoutType.CLASSIC_CHECKOUT,
      `${SERVER_BASE_URL}/request/checkout/readyToLeave`,
    ],
    [
      CheckoutType.BACKEND_DRIVEN_CHECKOUT,
      `${SERVER_BASE_URL}/request/state/readyToLeave`,
    ],
  ]);

  constructor(
    private http: HttpClient,
    private log: LoggingService,
    private iframeService: IframeService
  ) { }

  /**
   * Will attempt to navigate to the merchant success URL if possible.
   * The backend will call the merchant callback endpoint before allowing redirect.
   * If redirect is not possible (backend returns STAY) then the calling component needs
   * to handle this state, e.g. with manual navigation via a button.
   *
   * @return Observable of StayOrLeave - subscribe to get updates on the redirect status.
   * @param checkoutType the backend driven checkout has a different backend endpoint than the old checkout.
   * You may specify which system to talk to by setting the checkoutType.
   * @param ignoreDelayWhenLeaving set to true if the LEAVE response should instantly redirect, ignoring the "waitForMs" part of the response.
   */
  public async tryRedirect(
    checkoutType: CheckoutType = CheckoutType.BACKEND_DRIVEN_CHECKOUT,
    ignoreDelayWhenLeaving: boolean = false
  ): Promise<Observable<StayOrLeave>> {
    this.log.log(`Checkout trying to redirect to merchant (${checkoutType}).`);
    return new Observable<StayOrLeave>((subscriber) => {
      this.getReadyToLeave(
        subscriber,
        this.endpoints.get(checkoutType),
        Date.now(),
        ignoreDelayWhenLeaving
      );
    });
  }

  private async getReadyToLeave(
    subscriber: Subscriber<StayOrLeave>,
    endpoint: string,
    waitingSince: number,
    ignoreDelayWhenLeaving: boolean
  ) {
    if (subscriber.closed) {
      return;
    }

    const params = new HttpParams().append(
      'beenWaitingForMs',
      (Date.now() - waitingSince).toString()
    );
    await this.http
      .get<StayOrLeave>(endpoint, {
        params: params,
        withCredentials: true,
      })
      .toPromise()
      .then((stayOrLeave) => {
        if (subscriber.closed) {
          return;
        }

        subscriber.next(stayOrLeave);
        switch (stayOrLeave.then) {
          case WaitThen.WAIT:
            // Try again in a bit
            setTimeout(() => {
              this.getReadyToLeave(subscriber, endpoint, waitingSince, ignoreDelayWhenLeaving);
            }, stayOrLeave.waitForMs ?? 1000);
            break;
          case WaitThen.LEAVE:
            subscriber.complete();
            if (ignoreDelayWhenLeaving) {
              this.log.log(`Redirecting to merchant success URL.`);
              this.iframeService.brodcastSuccess(stayOrLeave.url);
            } else {
              this.log.log(
                `Redirecting to merchant success URL in ${stayOrLeave.waitForMs} ms.`
              );
              // Navigate to the success URL.
              setTimeout(() => {
                this.iframeService.brodcastSuccess(stayOrLeave.url);
              }, stayOrLeave.waitForMs ?? 50);
            }
            break;
          case WaitThen.STAY:
            // The backend couldn't call the merchant callback endpoint.
            this.log.log(
              `Redirect to merchant cancelled, merchant callback endpoint not reachable. Success URL available: ${!!stayOrLeave.url}`
            );
            subscriber.complete();
            break;
        }
      })
      .catch(() => {
        // Error: retry for 30 seconds.
        const retryForMs = 30000;
        const elapsedTime = Date.now() - waitingSince;
        if (elapsedTime < retryForMs) {
          subscriber.next({
            then: WaitThen.WAIT,
            waitForMs: retryForMs - elapsedTime,
          } as StayOrLeave);
          setTimeout(() => {
            this.getReadyToLeave(subscriber, endpoint, waitingSince, ignoreDelayWhenLeaving);
          }, 1000);
        } else {
          // There is no resolution to the error, signal STAY and let the component handle it.
          this.log.error(
            `Redirect to merchant cancelled, backend didn't respond`
          );
          subscriber.next({ then: WaitThen.STAY, waitForMs: 0 } as StayOrLeave);
          subscriber.complete();
        }
      });
  }
}

export enum CheckoutType {
  BACKEND_DRIVEN_CHECKOUT = 'BACKEND_DRIVEN_CHECKOUT',
  CLASSIC_CHECKOUT = 'CLASSIC_CHECKOUT',
}
